// ── Design decisions ──
// Mapeador PURO entre las tres fuentes de análisis (IA / texto pegado / Excel)
// y las filas que la agencia escribe en encomienda_packages y encomienda_items.
// Sin React, sin supabase: todo se prueba con datos planos.
//
// · Una sola forma de entrada: { extracted: { paquetes } }. Los tres parsers la
//   producen; acá ya no importa de dónde vino cada grupo (solo queda `source`
//   como chip de procedencia).
// · Auto-asignación de destinatario CONSERVADORA: se reutiliza autoMatch del
//   ExtractionDrawer (coincidencia exacta de todos los tokens, un empate nunca
//   decide). Nunca se crea un cliente ni una dirección: si no hay match, el
//   nombre leído queda como texto libre y la operadora decide.
// · Los tags de referencia (36H, AC12, #116) se quitan del nombre ANTES del
//   match: un "36h" dentro del nombre rompería la coincidencia exacta.
// · Destino: el primer grupo cae en el primer paquete vacío (el paquete 1 recién
//   creado en el paso 1); el resto crea paquetes nuevos. Nunca se pisa un
//   paquete que ya tiene artículos.
// · pending_review es la unión de: la bandera del parser, precio ausente y
//   unidad desconocida. La duda se marca, nunca se adivina un precio.
// · El escalón de impuesto sale SIEMPRE de calcPackageTax: acá no vive ningún
//   umbral en dólares (la tarifa de Maxi es privada y pertenece a
//   pricing_rules / customs_config).
// · mapItemAgency es el ÚNICO mapeador fila → estado de sesión: lo comparten
//   loadDraft, refreshPackageItems y el adaptador, y deriva unit_type del
//   embed customs_catalog en vez de fijarlo en POR_UNIDAD.

import { calcPackageTax, customsConfigComplete, packageFob } from '../../utils/customsCalc';
import { autoMatch } from '../ViajeroEncomienda/ExtractionDrawer';

export const MAX_DESC = 60;
export const UNIDADES = ['UNIDAD', 'KG', 'LB', 'PAR', 'JUEGO', 'DOCENA'];

const DELIVERY_DEFAULT = 'Drop Off';

const normalizar = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

// ── Unidades ─────────────────────────────────────────────────────────────────
const UNIDAD_RE = [
  [/^(kgs?|kilos?|kilogramos?|kilogramo)$/, 'KG'],
  [/^(lbs?|libras?|libra)$/, 'LB'],
  [/^(par|pares)$/, 'PAR'],
  [/^(juegos?)$/, 'JUEGO'],
  [/^(docenas?)$/, 'DOCENA'],
  [/^(unidad|unidades|und|uds?|pza|pzas|piezas?|u)$/, 'UNIDAD'],
];

export function normalizarUnidad(hint) {
  const limpio = normalizar(hint).replace(/\.+$/, '');
  if (!limpio) return { unidad: 'UNIDAD', desconocida: false };
  const hit = UNIDAD_RE.find(([re]) => re.test(limpio));
  if (hit) return { unidad: hit[1], desconocida: false };
  return { unidad: 'UNIDAD', desconocida: true };
}

// ── Nombre leído ─────────────────────────────────────────────────────────────
// Tag de referencia: "#116", "36H", "AC12", "36H." — mezcla de dígitos con # o
// letras. Un número pelado NO es tag (podría ser parte del nombre... no, pero
// tampoco lo tocamos: no es un tag de lista).
const TAG_TOKEN_RE = /^(#\d+[a-z]?|\d+[a-z]+|[a-z]{1,4}\d+[a-z]?)[.:\-)]?$/i;

export function limpiarNombre(raw) {
  return String(raw ?? '')
    .split(/\s+/)
    .filter((t) => t && !TAG_TOKEN_RE.test(t))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Hash local (FNV-1a 32 bits) ──────────────────────────────────────────────
export function hashLocal(str) {
  const s = String(str ?? '');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `local-${h.toString(16).padStart(8, '0')}`;
}

// ── Recipient helpers ────────────────────────────────────────────────────────
export const recipientVacio = () => ({
  name: '',
  phone: '',
  address: '',
  departamento: '',
  municipio: '',
  delivery_type: DELIVERY_DEFAULT,
});

export function recipientDesdeAddress(addr) {
  if (!addr) return recipientVacio();
  return {
    name: String(addr.recipient_name ?? '').trim(),
    phone: String(addr.recipient_phone ?? '').replace(/\D/g, ''),
    address: String(addr.direccion ?? '').trim(),
    departamento: String(addr.departamento ?? '').trim(),
    municipio: String(addr.municipio ?? '').trim(),
    delivery_type: DELIVERY_DEFAULT,
  };
}

export function recipientDesdePaqueteRow(row) {
  if (!row) return recipientVacio();
  return {
    name: String(row.recipient_name ?? '').trim(),
    phone: String(row.recipient_phone ?? '').replace(/\D/g, ''),
    address: String(row.recipient_address ?? '').trim(),
    departamento: String(row.recipient_departamento ?? '').trim(),
    municipio: String(row.recipient_municipio ?? '').trim(),
    delivery_type: row.delivery_type || DELIVERY_DEFAULT,
  };
}

const t = (v) => String(v ?? '').trim();

export function mismoRecipient(a, b) {
  const x = a || {};
  const y = b || {};
  return (
    t(x.name) === t(y.name) &&
    t(x.phone).replace(/\D/g, '') === t(y.phone).replace(/\D/g, '') &&
    t(x.address) === t(y.address) &&
    t(x.departamento) === t(y.departamento) &&
    t(x.municipio) === t(y.municipio) &&
    t(x.delivery_type || DELIVERY_DEFAULT) === t(y.delivery_type || DELIVERY_DEFAULT)
  );
}

// ── Extracción → grupos de revisión ──────────────────────────────────────────
const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function mapearExtraccionAGrupos(resultado, { destinatarios = [], paquetesVacios = [], source = 'ai' } = {}) {
  const paquetes = resultado?.extracted?.paquetes || [];
  const candidatos = (destinatarios || []).map((a) => ({ ...a, full_name: a.recipient_name || '' }));
  // Sólo el paquete 1 es destino por defecto (y sólo si está vacío): otros
  // paquetes vacíos quedan como opción en el selector, nunca como default.
  const primerVacio =
    (paquetesVacios || []).find((p) => Number(p.package_number) === 1) || null;

  return paquetes.map((p, gi) => {
    const nombre = limpiarNombre(p.owner_name_raw);
    const match = nombre ? autoMatch(nombre, candidatos) : null;
    const recipient = match
      ? recipientDesdeAddress(match)
      : { ...recipientVacio(), name: nombre };

    const items = (p.items || []).map((it, ii) => {
      const qty = numOrNull(it.qty ?? it.cantidad);
      const unitPrice = numOrNull(it.unit_price ?? it.valor_unitario);
      return {
        key: `g-${gi}-i-${ii}`,
        description_raw: String(it.description_raw ?? it.descripcion ?? it.description ?? ''),
        qty: qty === null || qty <= 0 ? 1 : qty,
        unit_type: it.unit_type ?? it.unidad_medida ?? it.unidad ?? null,
        unit_price: unitPrice,
        is_new: it.is_new === undefined || it.is_new === null ? true : Boolean(it.is_new),
        notes: it.notes ?? null,
        requires_review: Boolean(it.requires_review) || unitPrice === null,
        source: it.source || source,
      };
    });

    return {
      key: `g-${gi}`,
      owner_name_raw: String(p.owner_name_raw ?? ''),
      source,
      recipient,
      addressId: match ? match.id : null,
      auto: Boolean(match),
      destino:
        gi === 0 && primerVacio
          ? { tipo: 'existente', packageId: primerVacio.id }
          : { tipo: 'nuevo' },
      requires_review: Boolean(p.requires_review),
      items,
    };
  });
}

// ── Grupo → fila de encomienda_packages ──────────────────────────────────────
// Exactamente las columnas que escribe handleAddPackage.
export function grupoAPaqueteInsert(grupo, { encomiendaId, packageNumber, manifestId = null, defaultRecipient = null } = {}) {
  const r = grupo?.recipient && t(grupo.recipient.name) ? grupo.recipient : defaultRecipient || recipientVacio();
  const address = t(r.address);
  const departamento = t(r.departamento);
  const municipio = t(r.municipio);
  const phone = t(r.phone).replace(/\D/g, '');
  return {
    encomienda_id: encomiendaId,
    recipient_name: t(r.name),
    recipient_phone: phone || null,
    recipient_address: [address, departamento, municipio].filter(Boolean).join(', '),
    recipient_departamento: departamento || null,
    recipient_municipio: municipio || null,
    delivery_type: r.delivery_type || DELIVERY_DEFAULT,
    package_number: packageNumber,
    entry_manifest_id: manifestId ?? null,
  };
}

// ── Item de grupo → fila de encomienda_items ─────────────────────────────────
export function itemAInsert(item, { packageId, tenantId } = {}) {
  const raw = String(item?.description_raw ?? item?.descripcion ?? item?.description ?? '').trim();
  const qtyRaw = numOrNull(item?.qty ?? item?.cantidad);
  const qty = qtyRaw === null || qtyRaw <= 0 ? 1 : qtyRaw;
  const precio = numOrNull(item?.unit_price ?? item?.valor_unitario);
  const { unidad, desconocida } = normalizarUnidad(item?.unit_type ?? item?.unidad_medida ?? item?.unidad);
  return {
    package_id: packageId,
    quantity: qty,
    description: raw.slice(0, MAX_DESC),
    description_raw: raw,
    condition: item?.is_new === false ? 'USADO' : 'NUEVO',
    unit_value: precio === null ? 0 : precio,
    unidad_medida: unidad,
    is_merchandise: true,
    catalog_id: null,
    cip_usd: null,
    pending_review: Boolean(item?.requires_review) || precio === null || desconocida,
    tenant_id: tenantId ?? null,
  };
}

// ── Fila de encomienda_items → item de sesión (mapeador ÚNICO) ───────────────
export function mapItemAgency(row) {
  const cat = row?.customs_catalog || null;
  const catUnitType = cat?.unit_type ?? null;
  return {
    tempId: row.id,
    id: row.id,
    quantity: row.quantity ?? 1,
    description: row.description || '',
    description_raw: row.description_raw ?? '',
    unidad_medida: row.unidad_medida || 'UNIDAD',
    condition: row.condition || 'NUEVO',
    unitValue: String(row.unit_value ?? 0),
    catalog_id: row.catalog_id || null,
    cip_usd: row.cip_usd !== null && row.cip_usd !== undefined ? row.cip_usd : null,
    pending_review: Boolean(row.pending_review),
    is_merchandise: row.is_merchandise ?? true,
    catalogo_unit_type: catUnitType,
    catalogo_unidad_medida: cat?.unidad_medida ?? null,
    catalogo_descripcion: cat?.descripcion ?? null,
    unit_type: catUnitType || row.unit_type || 'POR_UNIDAD',
    source: 'manual',
  };
}

// ── Totales ──────────────────────────────────────────────────────────────────
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export function subtotalItems(items) {
  return (items || []).reduce((s, it) => s + num(it.quantity ?? it.qty) * num(it.unitValue ?? it.unit_value ?? it.unit_price), 0);
}

export function totalesGrupos(grupos) {
  const lista = grupos || [];
  const items = lista.flatMap((g) => g.items || []);
  return {
    paquetes: lista.length,
    articulos: items.length,
    porRevisar: items.filter((it) => it.requires_review).length,
    fob: items.reduce((s, it) => s + num(it.qty ?? it.quantity) * num(it.unit_price ?? it.unit_value ?? it.unitValue), 0),
  };
}

// Escalón de impuesto de UN paquete, derivado SIEMPRE por calcPackageTax (nunca
// un umbral acá). Nunca se le pasan artículos de varios paquetes juntos: el
// total de la encomienda se suma después, paquete por paquete.
// Sin config completa no hay impuesto que mostrar: sinConfig y ceros.
export function tierImpuesto(items, config) {
  const filas = (items || []).map((it) => ({
    quantity: it.quantity ?? it.qty ?? 1,
    unit_value: it.unitValue ?? it.unit_value ?? it.unit_price ?? 0,
  }));
  const fob = packageFob(filas);
  if (!customsConfigComplete(config)) {
    return { fob, dai: 0, iva: 0, total: 0, sobreUmbral: false, sinConfig: true, etiqueta: 'Sin config aduanal' };
  }
  const r = calcPackageTax(fob, config);
  const sobreUmbral = r.dai > 0;
  return {
    fob,
    dai: r.dai,
    iva: r.iva,
    total: r.total,
    sobreUmbral,
    sinConfig: false,
    etiqueta: sobreUmbral ? 'DAI + IVA' : 'IVA',
  };
}
